import {
  applyConditionalCategoryProperties,
  applyElementCategory,
  applyElementCategoryPropertySelections,
  findElementCategoryAndSelectionsByProductId,
  getCategorySourcedPropertiesInTemplateOrder,
  getCategoryPropertyValueRecord,
  isAutoGeneratedChildrenCategory,
} from './element-category.helpers';
import { createElement } from './element-factory.helpers';
import {
  getElementProperty,
  getElementPropertyResolvedCountRecord,
  isElementSelectProperty,
  isElementSwitchProperty,
  setElementProperties,
  type PropertyChange,
} from './element-property.helpers';
import { getAllProductElements, isElement } from './recursive-element.helpers';
import type { ElementCategoryID } from '../models/element-categories.model';
import type {
  IElementProperty,
  PropertyResolvedCountRecord,
} from '../models/element-property.model';
import type { IProduct, ProductID } from '../models/product.model';
import type { IElement } from '../models/project.model';

/** In-memory id for conditional evaluation only — never persisted on a project. */
export const GENERIC_PRODUCT_PICKER_ELEMENT_ID = 'generic_product_picker';

export type GenericProductPickerChange = {
  product_id: ProductID | undefined;
  category_id?: ElementCategoryID;
  category_property_value_record: IProduct['category_property_value_record'];
};

export type CategoryPickerState = {
  category_id?: ElementCategoryID;
  category_property_value_record: PropertyResolvedCountRecord;
};

/**
 * Build a transient element so category templates and {@link IConditionalNode} rules
 * can run via existing helpers (`filterConditionalProperties`, `getChildElements`).
 */
export const buildCategoryPickerElement = (
  category_id: ElementCategoryID | undefined,
  record: PropertyResolvedCountRecord = {},
): IElement => {
  let element = applyElementCategory(
    createElement({ id: GENERIC_PRODUCT_PICKER_ELEMENT_ID }, false),
    isAutoGeneratedChildrenCategory(category_id) ? category_id : undefined,
  );

  if (category_id && Object.keys(record).length > 0) {
    try {
      element = applyElementCategoryPropertySelections(element, record);
    } catch {
      // Incompatible keys and invalid options are skipped earlier; a thrown apply leaves the category without the record.
    }
  }

  return applyConditionalCategoryProperties(element);
};

/**
 * Category-backed select/switch rows from the template, after conditional filtering.
 */
export const getCategoryPickerVisibleProperties = (
  element: IElement,
  excludePropertyNames: readonly string[] = [],
): IElementProperty[] => {
  const excluded = new Set(excludePropertyNames);
  return getCategorySourcedPropertiesInTemplateOrder(element).filter(
    (property) =>
      !property.hidden &&
      !excluded.has(property.name) &&
      (isElementSelectProperty(property) || isElementSwitchProperty(property)),
  );
};

/**
 * Initial picker state from an element or product being edited.
 *
 * When creating a new EPD, `defaults` is the parent product-category element
 * (e.g. Concrete) and not the product being created, so its category is only a
 * rough hint — a rebar EPD added to a Concrete element's reinforcement slot
 * would inherit Concrete/C30/37. The generic product being added identifies the
 * category and selections exactly, so it wins whenever it resolves.
 *
 * When editing an existing product, `defaults` is that product and its stored
 * category and property record must be preserved as-is.
 */
export const createCategoryPickerStateFromDefaults = (
  defaults?: IElement | IProduct,
  genericProductId?: ProductID,
): CategoryPickerState => {
  const category_id = defaults?.category_id;

  const genericMatch =
    genericProductId && isElement(defaults)
      ? findElementCategoryAndSelectionsByProductId(genericProductId)
      : undefined;

  if (genericMatch) {
    const element = buildCategoryPickerElement(
      genericMatch.category_id,
      genericMatch.selections,
    );
    return {
      category_id: genericMatch.category_id,
      category_property_value_record: getCategoryPropertyValueRecord(element),
    };
  }

  const record = isElement(defaults)
    ? getCategoryPropertyValueRecord(defaults)
    : (defaults?.category_property_value_record ?? {});

  if (!category_id || !isAutoGeneratedChildrenCategory(category_id)) {
    return { category_id: undefined, category_property_value_record: {} };
  }

  const element = buildCategoryPickerElement(category_id, record);
  return {
    category_id,
    category_property_value_record: getCategoryPropertyValueRecord(element),
  };
};

/**
 * Apply a new product/service category and re-derive the property record.
 */
export const applyCategoryPickerCategoryId = (
  category_id: ElementCategoryID,
  previousRecord: PropertyResolvedCountRecord = {},
): CategoryPickerState => {
  const element = buildCategoryPickerElement(category_id, previousRecord);
  return {
    category_id,
    category_property_value_record: getCategoryPropertyValueRecord(element),
  };
};

/**
 * Update one category property value and re-run conditional property rules.
 */
export const applyCategoryPickerPropertyChange = (
  category_id: ElementCategoryID,
  record: PropertyResolvedCountRecord,
  change: PropertyChange,
): PropertyResolvedCountRecord => {
  let element = buildCategoryPickerElement(category_id, record);
  const property = getElementProperty(element, change.name, false);
  if (!property) return record;

  element = setElementProperties(element, element, change);
  element = applyConditionalCategoryProperties(element);
  return getElementPropertyResolvedCountRecord(
    getCategorySourcedPropertiesInTemplateOrder(element),
  );
};

/**
 * Resolve generic product id from category child definitions (conditional product nodes).
 */
export const resolveCategoryPickerProductId = (
  category_id: ElementCategoryID | undefined,
  record: PropertyResolvedCountRecord,
  genericProductId?: string,
): ProductID | undefined => {
  if (!category_id) return undefined;

  const element = buildCategoryPickerElement(category_id, record);
  const productElements = getAllProductElements(element);
  const genericMatch = productElements.find(
    (child) => child.product_id === genericProductId,
  );
  const productElement = genericMatch ?? productElements[0];
  return productElement?.generic_product_id ?? productElement?.product_id;
};

/**
 * Snapshot emitted to {@link EditProductDialog} on save.
 */
export const getGenericProductPickerChange = (
  state: CategoryPickerState,
  genericProductId?: string,
): GenericProductPickerChange => ({
  product_id: resolveCategoryPickerProductId(
    state.category_id,
    state.category_property_value_record,
    genericProductId,
  ),
  category_id: state.category_id,
  category_property_value_record: state.category_property_value_record,
});
